import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { theme, cardStyle, statCard, tdStyle, thStyle } from '../theme';

const ExpenseAnalyzerPage = ({ API_BASE }) => {
  const [tab, setTab] = useState('overview');
  const [trends, setTrends] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [billAnalysis, setBillAnalysis] = useState(null);

  const fetchAll = async () => {
    try {
      const [t, v, c, p, b] = await Promise.all([
        axios.get(`${API_BASE}/expense-analyzer/monthly-trends`),
        axios.get(`${API_BASE}/expense-analyzer/vendor-analysis`),
        axios.get(`${API_BASE}/expense-analyzer/category-breakdown`),
        axios.get(`${API_BASE}/expense-analyzer/predictions`),
        axios.get(`${API_BASE}/expense-analyzer/bill-analysis`),
      ]);
      setTrends(t.data); setVendors(v.data); setCategories(c.data); setPredictions(p.data); setBillAnalysis(b.data);
    } catch (e) {}
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fmtINR = n => `₹${(n || 0).toLocaleString('en-IN')}`;
  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)}
      style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.86rem',
        backgroundColor: tab === key ? theme.primary : '#e9ecef', color: tab === key ? 'white' : '#555' }}>
      {label}
    </button>
  );

  const insightColors = { warning: '#f59e0b', alert: '#ef4444', info: '#3b82f6', success: '#10b981' };

  return (
    <div>
      {/* KPI from predictions */}
      {predictions && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '20px' }}>
          <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Predicted Expense</span>
            <h2 style={{ margin: '4px 0 0', color: theme.danger, fontSize: '1.4rem', fontWeight: '700' }}>{fmtINR(predictions.predictedExpense)}</h2>
            <span style={{ fontSize: '0.72rem', color: theme.textSubtle }}>{predictions.nextMonth}</span></div>
          <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Predicted Revenue</span>
            <h2 style={{ margin: '4px 0 0', color: theme.success, fontSize: '1.4rem', fontWeight: '700' }}>{fmtINR(predictions.predictedRevenue)}</h2></div>
          <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Predicted Profit</span>
            <h2 style={{ margin: '4px 0 0', color: predictions.predictedProfit >= 0 ? theme.success : theme.danger, fontSize: '1.4rem', fontWeight: '700' }}>{fmtINR(predictions.predictedProfit)}</h2></div>
          <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Total Vendors</span>
            <h2 style={{ margin: '4px 0 0', color: theme.navy, fontSize: '1.4rem', fontWeight: '700' }}>{vendors.length}</h2></div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {tabBtn('overview', '📊 Overview')}
        {tabBtn('predictions', '🤖 AI Predictions')}
        {tabBtn('vendors', '🏪 Vendor Analysis')}
        {tabBtn('categories', '📂 Category Breakdown')}
        {tabBtn('bills', '🧾 Bill Analysis')}
      </div>

      {/* OVERVIEW — Revenue vs Expense chart */}
      {tab === 'overview' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', color: theme.navy }}>Revenue vs Expenses (Last 12 Months)</h3>
          {trends.length > 0 ? (
            <div style={{ height: '350px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={theme.success} stopOpacity={0.3} /><stop offset="100%" stopColor={theme.success} stopOpacity={0} /></linearGradient>
                    <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={theme.danger} stopOpacity={0.3} /><stop offset="100%" stopColor={theme.danger} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.borderLight} />
                  <XAxis dataKey="label" stroke={theme.textMuted} fontSize={12} />
                  <YAxis stroke={theme.textMuted} fontSize={12} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: `1px solid ${theme.border}` }} formatter={v => fmtINR(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke={theme.success} fill="url(#gRev)" strokeWidth={2} />
                  <Area type="monotone" dataKey="expenses" name="Expenses" stroke={theme.danger} fill="url(#gExp)" strokeWidth={2} />
                  <Area type="monotone" dataKey="profit" name="Profit" stroke={theme.info} fill="none" strokeWidth={2} strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <p style={{ color: '#aaa' }}>No data yet</p>}
        </div>
      )}

      {/* AI PREDICTIONS */}
      {tab === 'predictions' && predictions && (
        <div>
          {/* Insights */}
          <div style={{ ...cardStyle, marginBottom: '18px' }}>
            <h3 style={{ margin: '0 0 14px', color: theme.navy }}>🤖 AI Insights</h3>
            {predictions.insights.map((ins, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', marginBottom: '6px', borderRadius: '8px', backgroundColor: (insightColors[ins.type] || '#aaa') + '12', borderLeft: `4px solid ${insightColors[ins.type] || '#aaa'}` }}>
                <span style={{ fontSize: '1.1rem' }}>{ins.type === 'warning' ? '⚠️' : ins.type === 'alert' ? '🚨' : ins.type === 'success' ? '✅' : 'ℹ️'}</span>
                <span style={{ fontSize: '0.88rem', color: theme.navy }}>{ins.text}</span>
              </div>
            ))}
          </div>

          {/* Category predictions */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 14px', color: theme.navy }}>Category-wise Forecast — {predictions.nextMonth}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Last Month</th><th style={{ ...thStyle, textAlign: 'right' }}>Predicted</th><th style={{ ...thStyle, textAlign: 'right' }}>Trend</th></tr></thead>
              <tbody>{predictions.categoryPredictions.map(cp => (
                <tr key={cp.category}><td style={tdStyle}>{cp.category}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtINR(cp.lastMonth)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{fmtINR(cp.predicted)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: cp.trend > 0 ? theme.danger : theme.success, fontWeight: '600' }}>{cp.trend > 0 ? '▲' : cp.trend < 0 ? '▼' : '—'} {Math.abs(cp.trend)}%</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* VENDOR ANALYSIS */}
      {tab === 'vendors' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 14px', color: theme.navy }}>Vendor-wise Expense Analysis</h3>
          {vendors.length === 0 ? <p style={{ color: '#aaa' }}>No vendor data</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Vendor</th><th style={thStyle}>Top Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Total Spent</th><th style={{ ...thStyle, textAlign: 'right' }}>Avg/Month</th><th style={{ ...thStyle, textAlign: 'right' }}>Transactions</th></tr></thead>
              <tbody>{vendors.map(v => (
                <tr key={v.vendor}><td style={{ ...tdStyle, fontWeight: '600' }}>{v.vendor}</td>
                  <td style={tdStyle}>{v.topCategory}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: theme.danger }}>{fmtINR(v.total)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtINR(v.avgPerMonth)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{v.count}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* CATEGORY BREAKDOWN */}
      {tab === 'categories' && categories && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
            <h3 style={{ margin: 0, color: theme.navy }}>Category Breakdown</h3>
            <div style={{ display: 'flex', gap: '14px' }}>
              <span style={{ fontSize: '0.85rem' }}>This Month: <strong style={{ color: theme.danger }}>{fmtINR(categories.thisMonthTotal)}</strong></span>
              <span style={{ fontSize: '0.85rem' }}>Last Month: <strong>{fmtINR(categories.lastMonthTotal)}</strong></span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>This Month</th><th style={{ ...thStyle, textAlign: 'right' }}>Last Month</th><th style={{ ...thStyle, textAlign: 'right' }}>All Time</th><th style={{ ...thStyle, textAlign: 'right' }}>Change</th></tr></thead>
            <tbody>{categories.categories.map(c => (
              <tr key={c.category}><td style={{ ...tdStyle, fontWeight: '600' }}>{c.category}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{fmtINR(c.thisMonth)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtINR(c.lastMonth)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtINR(c.allTime)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: c.change > 0 ? theme.danger : theme.success, fontWeight: '600' }}>{c.change > 0 ? '▲' : c.change < 0 ? '▼' : '—'} {Math.abs(c.change)}%</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* BILL ANALYSIS */}
      {tab === 'bills' && billAnalysis && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '18px' }}>
            <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Total Bills</span><h2 style={{ margin: '4px 0 0', color: theme.navy }}>{billAnalysis.total}</h2></div>
            <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Average Bill</span><h2 style={{ margin: '4px 0 0', color: theme.info }}>{fmtINR(billAnalysis.avg)}</h2></div>
            <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Highest Bill</span><h2 style={{ margin: '4px 0 0', color: theme.success }}>{fmtINR(billAnalysis.max)}</h2></div>
            <div style={statCard}><span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>Lowest Bill</span><h2 style={{ margin: '4px 0 0', color: theme.warning }}>{fmtINR(billAnalysis.min)}</h2></div>
          </div>
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 14px', color: theme.navy }}>Top 10 Highest Bills</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>#</th><th style={thStyle}>Patient</th><th style={thStyle}>Items</th><th style={thStyle}>Date</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>{(billAnalysis.topBills || []).map((b, i) => (
                <tr key={i}><td style={tdStyle}>{i + 1}</td><td style={{ ...tdStyle, fontWeight: '600' }}>{b.patient}</td><td style={tdStyle}>{b.items}</td><td style={tdStyle}>{b.date ? new Date(b.date).toLocaleDateString('en-IN') : '—'}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: theme.success }}>{fmtINR(b.amount)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseAnalyzerPage;
